import { useState, useEffect, useCallback, useRef } from "react";
import {
    Alert,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Table,
} from "reactstrap";

import { ISelection } from "Pages/Tools/MatchTools/MatchToolsSetup";
import styles from "Pages/Tools/MatchTools/Components/SelectModal/SelectModal.module.css";
import CacheTimeAndRefreshButton from "Pages/Tools/MatchTools/Components/SelectModal/CacheTimeAndRefreshButton";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";

interface IProps {
    modal: boolean;
    onSetModal: (modal: boolean) => void;
    selectData: ISelection[];
    onSetSelection: (selection: ISelection | undefined) => void;
    description: string;
    cacheTime: string;
    getSelectData: (reset_cache: boolean) => void;
    isLoading: boolean;
    /** Why the load failed, if it did. Empty string means the load succeeded. */
    error?: string;
}

export default function SelectModal({
    modal,
    onSetModal,
    selectData,
    onSetSelection,
    description,
    cacheTime,
    getSelectData,
    isLoading,
    error = "",
}: IProps) {
    const [clicked, setClicked] = useState<ISelection>();
    const [searchterm, setSearchTerm] = useState("");

    const inputRef = useRef<HTMLInputElement>(null);

    const selectProject = useCallback((project?: ISelection) => {
        const projectToSelect = project || clicked;
        if (projectToSelect) {
            const newClicked = { ...projectToSelect };
            onSetSelection(newClicked);
            onSetModal(false);
        }
    }, [clicked, onSetModal, onSetSelection]);

    // Autofocus the search input when the modal opens. Doing it this way because autoFocus
    // prop on Input did not work, probably because of the way the modal is rendered after a fetch.
    useEffect(() => {
        if (modal) {
            const timeout = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 300);

            return () => clearTimeout(timeout);
        }
    }, [modal]);

    // Add event listener for enter key to trigger submit button from search input
    useEffect(() => {
        const handleEnterKey = (e: KeyboardEvent) => {
            if (e.key === "Enter" && clicked) {
                selectProject();
            }
        };

        document.addEventListener("keydown", handleEnterKey);

        return () => {
            document.removeEventListener("keydown", handleEnterKey);
        };
    }, [clicked, selectProject]);

    const handleClick = (d: ISelection) => {
        setClicked((prev) => (prev?.name === d.name ? undefined : d));
    };

    const toggle = () => {
        onSetModal(!modal);
    };

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const selectDataShown = selectData.filter(
        (d) =>
            d.name.toLowerCase().includes(searchterm.toLowerCase()) ||
            d.project_number.toLowerCase().includes(searchterm.toLowerCase()) ||
            d.project_id.toLowerCase().includes(searchterm.toLowerCase())
    );

    useEffect(() => {
        const handleEnterKey = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                if (selectDataShown.length === 1) {
                    setClicked(selectDataShown[0]);
                    selectProject(selectDataShown[0]);

                } else if (clicked) {
                    selectProject();
                }
            }
        };

        document.addEventListener("keydown", handleEnterKey);

        return () => {
            document.removeEventListener("keydown", handleEnterKey);
        };
    }, [clicked, selectProject, selectDataShown]);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {description}
                <CacheTimeAndRefreshButton
                    cacheTime={cacheTime}
                    getSelectData={getSelectData}
                />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                {modal && (
                    <Input
                        innerRef={inputRef}
                        type="search"
                        placeholder="Search..."
                        value={searchterm}
                        onChange={handleSearchTermChange}
                        className={styles.searchInput}
                        style={{ boxShadow: "var(--boxShadow)" }}
                    />
                )}
                {isLoading ? (
                    <DimensionSpinner style={{ marginTop: "15px" }} />
                ) : error ? (
                    // dimension-mrzt3: a failed load used to render as an empty table, which reads
                    // as "there is nothing to pick" instead of "this did not work".
                    <Alert color="danger" className={styles.loadError}>
                        <strong>Could not load this list.</strong>
                        <div>{error}</div>
                        <div>Use the refresh button above to try again.</div>
                    </Alert>
                ) : selectDataShown.length === 0 ? (
                    <Alert color="secondary" className={styles.loadError}>
                        {selectData.length === 0
                            ? "No records were returned."
                            : "No records match your search."}
                    </Alert>
                ) : (
                    <Table striped hover>
                        <tbody>
                            {selectDataShown.map((d) => (
                                <tr
                                    key={d.project_id}
                                    onClick={() => handleClick(d)}
                                    style={
                                        clicked?.project_id === d.project_id
                                            ? {
                                                  backgroundColor:
                                                      "var(--dimensionBlueHighlight)",
                                              }
                                            : {}
                                    }
                                >
                                    <td>
                                        {d.project_number} - {d.name}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="primary"
                    disabled={!clicked}
                    onClick={() => selectProject()}
                >
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
